/**
 * Thin fetch wrapper for the C17 office API under `/api/office/v1` (ADR-06 §2.3).
 * The session cookie is set by the server (`__Host-fs`, SameSite=Strict); every
 * mutating call carries `x-csrf-token` from the login / session response. Errors
 * arrive as `{ error: { code, params } }` and surface as `ApiClientError`, which
 * the UI translates through `error.<CODE>` keys.
 */
import { currentTrace, reportError } from "../app/telemetry.js";

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    readonly params: Record<string, unknown>,
    readonly status: number,
    readonly requestId?: string,
    /** [C28] D-259: the trace id of the request (the screen's), shown as "رقم التتبع" for unexpected errors. */
    readonly traceId?: string,
  ) {
    super(code);
    this.name = "ApiClientError";
  }
}

export function isApiClientError(e: unknown): e is ApiClientError {
  return e instanceof ApiClientError;
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  /** [C35] a binary answer (the server PDF, D-146); errors as JSON like every call. */
  postBlob(path: string, body?: unknown): Promise<Blob>;
  setCsrfToken(token: string | null): void;
}

export function createApiClient(base = "/api/office/v1"): ApiClient {
  let csrf: string | null = null;

  async function call<T>(method: string, path: string, body?: unknown, binary = false): Promise<T> {
    // [C28] D-259: the screen's trace id and name on every call
    const trace = currentTrace();
    const headers: Record<string, string> = { accept: "application/json", "x-faylasuf-trace": trace.traceId, "x-faylasuf-screen": trace.screen };
    if (body !== undefined) headers["content-type"] = "application/json";
    if (method !== "GET" && csrf) headers["x-csrf-token"] = csrf;
    let res: Response;
    try {
      res = await fetch(base + path, {
        method,
        headers,
        credentials: "same-origin",
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch (e) {
      reportError("failed_request", e, { status: 0, path: base + path, traceId: trace.traceId });
      throw new ApiClientError("NETWORK", {}, 0, undefined, trace.traceId);
    }
    if (binary && res.ok) return (await res.blob()) as T;
    const text = await res.text();
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }
    if (!res.ok) {
      const env = json as { error?: { code?: string; params?: Record<string, unknown> }; requestId?: string; traceId?: string } | null;
      const err = new ApiClientError(env?.error?.code ?? "UNKNOWN", env?.error?.params ?? {}, res.status, env?.requestId, env?.traceId ?? res.headers.get("x-faylasuf-trace") ?? trace.traceId);
      if (res.status >= 500) reportError("failed_request", err, { status: res.status, path: base + path, traceId: err.traceId! });
      throw err;
    }
    return json as T;
  }

  return {
    get: (path) => call("GET", path),
    post: (path, body) => call("POST", path, body ?? {}),
    put: (path, body) => call("PUT", path, body ?? {}),
    patch: (path, body) => call("PATCH", path, body ?? {}),
    postBlob: (path, body) => call<Blob>("POST", path, body ?? {}, true),
    setCsrfToken: (token) => {
      csrf = token;
    },
  };
}
